import { CommonModule, DOCUMENT } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	signal,
	ElementRef,
	effect,
	viewChildren,
	viewChild,
	forwardRef,
	untracked,
	OnInit,
	model,
	inject,
	OnDestroy,
} from '@angular/core';
import { CdkListboxModule } from '@angular/cdk/listbox';
import { AvatarComponent } from '@ui/avatar/avatar.component';
import { IconsModule } from '@ui/icons/icons.module';
import { AVATAR_VARIANT_ORDER } from '@ui/utils/constants';
import { trigger, transition, style, animate } from '@angular/animations';
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	FormsModule,
} from '@angular/forms';

export interface SelectOption {
	text: string;
	id: string;
	avatarInitials?: string;
	avatarSrc?: string;
}

@Component({
	selector: 'ui-select',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		CdkListboxModule,
		AvatarComponent,
		IconsModule,
	],
	templateUrl: './select.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'w-full',
	},
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectComponent),
			multi: true,
		},
	],
	animations: [
		trigger('dropdown', [
			transition(':enter', [
				style({ opacity: 0, transform: 'scale(0.95)' }),
				animate(
					'180ms cubic-bezier(0.4,0,0.2,1)',
					style({ opacity: 1, transform: 'scale(1)' }),
				),
			]),
			transition(':leave', [
				animate(
					'120ms cubic-bezier(0.4,0,0.2,1)',
					style({ opacity: 0, transform: 'scale(0.95)' }),
				),
			]),
		]),
	],
})
export class SelectComponent
	implements ControlValueAccessor, OnInit, OnDestroy
{
	private document = inject(DOCUMENT);
	private hostElement = inject(ElementRef);
	public selected_ = model<SelectOption | SelectOption[]>(null, {
		alias: 'selected',
	});
	public disabled = input(false);
	public label = input<string>(null);
	public size = input<'sm' | 'md' | 'lg'>('md');
	public options = input.required<SelectOption[]>();
	public mode = input<'default' | 'multi'>('default');
	public searchable = input<boolean>(false);
	public allowCustom = input<boolean>(false);
	public customValueValidator = input<(value: string) => boolean>();

	public placeholder_ = input<string>('No options available');
	public isExpanded_ = signal(false);
	public searchQuery_ = signal<string>('');

	public hasOptions_ = computed(() => this.options()?.length > 0);
	public filteredOptions_ = computed(() => {
		const query = this.searchQuery_().toLowerCase().trim();
		if (!query) return this.options();
		return this.options().filter((opt) =>
			opt.text.toLowerCase().includes(query),
		);
	});
	public isMultiMode_ = computed(() => this.mode() === 'multi');
	public selectedArray_ = computed(() => {
		const selected = this.selected_();
		if (this.isMultiMode_()) {
			return Array.isArray(selected) ? selected : [];
		}
		return [];
	});
	public selectedSingle_ = computed(() => {
		const selected = this.selected_();
		if (!this.isMultiMode_() && !Array.isArray(selected)) {
			return selected;
		}
		return null;
	});
	public ariaControlsId = Math.random().toString(36).substring(2, 15);

	private optionBtns = viewChildren<ElementRef<HTMLButtonElement>>('optionBtn');
	private triggerButton =
		viewChild<ElementRef<HTMLButtonElement>>('triggerButton');
	private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	private onChange: ((value: string | string[]) => void) | undefined;
	private onTouched: VoidFunction | undefined;

	constructor() {
		effect(() => this.focusFirstOrSelectedOption());
		effect(() => {
			if (!this.hasOptions_()) {
				this.selected_.set(this.isMultiMode_() ? [] : null);
				this.onChange?.(this.isMultiMode_() ? [] : null);
			} else if (!this.isMultiMode_()) {
				const selected = untracked(this.selected_) as SelectOption;
				if (selected && !this.options().some((opt) => opt.id === selected.id)) {
					this.setFirstOptionAsSelected();
					return;
				}
			}
		});

		effect(() => {
			if (this.isExpanded_()) {
				setTimeout(
					() =>
						this.document.addEventListener('click', this.handleClickOutside),
					0,
				);
			} else {
				this.document.removeEventListener('click', this.handleClickOutside);
			}
		});
	}

	ngOnInit() {
		if (this.isMultiMode_()) {
			if (!this.selected_()) {
				this.selected_.set([]);
			}
		} else if (this.hasOptions_() && !this.selected_()) {
			this.setFirstOptionAsSelected();
		}
	}

	ngOnDestroy() {
		this.document.removeEventListener('click', this.handleClickOutside);
	}

	private handleClickOutside = (event: MouseEvent) => {
		const target = event.target as HTMLElement;
		if (!this.hostElement.nativeElement.contains(target)) {
			this.isExpanded_.set(false);
			this.onTouched?.();
		}
	};

	writeValue(value: string | string[]): void {
		if (this.isMultiMode_()) {
			const valueArray = Array.isArray(value) ? value : value ? [value] : [];
			const selectedOptions = valueArray
				.map((id) => this.options().find((opt) => opt.id === id))
				.filter((opt) => opt !== undefined) as SelectOption[];
			this.selected_.set(selectedOptions);
		} else {
			if (value && this.hasOptions_()) {
				const option = this.options().find((opt) => opt.id === value);
				if (option) {
					this.selected_.set(option);
				}
			}
		}
	}

	registerOnChange(fn: (value: string | string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: VoidFunction): void {
		this.onTouched = fn;
	}

	onClick() {
		if (!this.hasOptions_() && !this.allowCustom()) {
			return;
		}
		this.isExpanded_.update((prev) => !prev);
	}

	focusSearchInput() {
		if (this.disabled()) return;
		setTimeout(() => this.searchInput()?.nativeElement?.focus(), 0);
	}

	onSearchFocus() {
		if (!this.isExpanded_()) {
			this.isExpanded_.set(true);
		}
	}

	onSearchBlur() {
		setTimeout(() => {
			if (
				!this.document.activeElement ||
				!this.document.activeElement.closest(`#${this.ariaControlsId}`)
			) {
				this.isExpanded_.set(false);
				this.onTouched?.();
			}
		}, 200);
	}

	onListboxFocusOut(event: FocusEvent) {
		const newFocusTarget = event.relatedTarget as HTMLElement;
		const currentTarget = event.currentTarget as HTMLElement;

		if (
			!currentTarget.contains(newFocusTarget) &&
			newFocusTarget !== this.triggerButton()?.nativeElement &&
			newFocusTarget !== this.searchInput()?.nativeElement
		) {
			if (
				!this.isMultiMode_() ||
				!currentTarget.parentElement?.contains(newFocusTarget)
			) {
				this.isExpanded_.set(false);
				this.onTouched?.();
			}
		}
	}

	onSelect(items: SelectOption[]) {
		if (!this.hasOptions_()) {
			return;
		}
		const item = items[0];

		if (this.isMultiMode_()) {
			const currentSelected = this.selectedArray_();
			const isSelected = currentSelected.some((opt) => opt.id === item.id);

			let newSelected: SelectOption[];
			if (isSelected) {
				newSelected = currentSelected.filter((opt) => opt.id !== item.id);
			} else {
				newSelected = [...currentSelected, item];
			}

			this.selected_.set(newSelected);
			this.onChange?.(newSelected.map((opt) => opt.id));
			this.searchQuery_.set('');
		} else {
			this.selected_.set(item);
			this.isExpanded_.set(false);
			this.onChange?.(item.id);
			this.onTouched?.();
			this.triggerButton()?.nativeElement?.focus();
		}
	}

	onKeyDown(event: KeyboardEvent) {
		if (!this.hasOptions_()) {
			return;
		}
		switch (event.key) {
			case 'Escape':
				this.isExpanded_.set(false);
				this.triggerButton()?.nativeElement?.focus();
				break;
			case 'Tab':
				this.isExpanded_.set(false);
				break;
			default:
				break;
		}
	}

	compareOptions(a: SelectOption, b: SelectOption): boolean {
		return a.id === b.id;
	}

	removeSelected(option: SelectOption, event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		const currentSelected = this.selectedArray_();
		const newSelected = currentSelected.filter((opt) => opt.id !== option.id);
		this.selected_.set(newSelected);
		this.onChange?.(newSelected.map((opt) => opt.id));
	}

	onSearchInput(value: string): void {
		this.searchQuery_.set(value);
	}

	onSearchKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter' || event.key === ',') {
			event.preventDefault();
			const query = this.searchQuery_().trim();

			if (query) {
				const matchingOption = this.filteredOptions_().find(
					(opt) => opt.text.toLowerCase() === query.toLowerCase(),
				);

				if (matchingOption) {
					this.onSelect([matchingOption]);
				} else if (this.allowCustom()) {
					const validator = this.customValueValidator();
					if (!validator || validator(query)) {
						this.addCustomValue(query);
					}
				}
			}
		} else if (event.key === 'Escape') {
			if (this.searchQuery_()) {
				this.searchQuery_.set('');
				event.stopPropagation();
			} else {
				this.isExpanded_.set(false);
				this.searchInput()?.nativeElement?.blur();
			}
		} else if (
			event.key === 'Backspace' &&
			!this.searchQuery_() &&
			this.selectedArray_().length > 0
		) {
			event.preventDefault();
			const lastItem = this.selectedArray_()[this.selectedArray_().length - 1];
			this.removeSelected(lastItem);
		}
	}

	addCustomValue(value: string): void {
		if (!value.trim()) return;

		const currentSelected = this.selectedArray_();
		const alreadySelected = currentSelected.some((opt) => opt.id === value);

		if (!alreadySelected) {
			const newOption: SelectOption = { id: value, text: value };
			const newSelected = [...currentSelected, newOption];
			this.selected_.set(newSelected);
			this.onChange?.(newSelected.map((opt) => opt.id));
		}

		this.searchQuery_.set('');
	}

	isOptionSelected(option: SelectOption): boolean {
		if (this.isMultiMode_()) {
			return this.selectedArray_().some((opt) => opt.id === option.id);
		}
		const selected = this.selected_() as SelectOption;
		return selected?.id === option.id;
	}

	public avatarSize_ = computed(() => {
		return {
			sm: null,
			md: 'sm',
			lg: 'md',
		}[this.size() ?? 'md'];
	});

	public buttonTriggerVariant_ = computed(() => {
		const selected = this.selected_();
		if (Array.isArray(selected)) {
			return null;
		}
		const selectedOptionIndex = this.options().findIndex(
			(o) => o.id === selected?.id,
		);
		if (selectedOptionIndex === -1) {
			return null;
		}

		return this.variantOrderList[
			selectedOptionIndex % this.variantOrderList.length
		];
	});

	public buttonClasses_ = computed(() => [
		this.commonButtonClasses,
		this.getSizeClass_(),
		this.getDisabledClass_(),
	]);

	public getSizeClass_ = computed(() =>
		this.size() === 'lg' ? 'text-base' : 'text-sm',
	);

	public variantOrderList = AVATAR_VARIANT_ORDER;

	private commonButtonClasses = [
		'flex items-center gap-x-2 p-2 duration-300 w-full justify-between rounded-md text-gray-500 pl-[10px] pr-3',
		'[&_span]:truncate',
		'[&>div]:flex',
		'[&>div]:items-center',
		'[&>div]:gap-x-2',
		'[&>div]:truncate',
		'[&>div]:justify-between',
		'[&>div]:text-left',
		'[&>i-tabler]:w-4',
		'[&>i-tabler]:h-4',
		'hover:bg-gray-50',
		'outline-none',
		'focus-visible:ring-2',
		'focus-visible:ring-offset-4',
		'focus-visible:ring-green-light',
	].join(' ');

	private setFirstOptionAsSelected() {
		this.selected_.set(this.options()[0]);
	}

	private getDisabledClass_ = computed(() =>
		this.disabled() ? 'bg-gray-50 cursor-not-allowed' : '',
	);

	private focusFirstOrSelectedOption() {
		const btns = this.optionBtns();
		if (btns) {
			let focusIndex = 0;
			const selectedIdx = btns.findIndex(
				(btn) => btn.nativeElement.getAttribute('aria-selected') === 'true',
			);
			if (selectedIdx !== -1) {
				focusIndex = selectedIdx;
			}
			btns[focusIndex]?.nativeElement?.focus?.();
		}
	}
}
